package com.eladfinish.bible.contest.screens.search.usecases

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.analytics.crashlytics.ExceptionReporter
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.data.common.stringResolver.IStringResolver
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.illegal_name_to_be_searched
import com.eladfinish.bible.contest.illegal_regular_expression
import com.eladfinish.bible.contest.screens.search.model.ResultRegex
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import dev.icerock.moko.resources.desc.Resource
import dev.icerock.moko.resources.desc.StringDesc
import io.github.aakira.napier.Napier

class SearchUseCase(
    private val stringResolver: IStringResolver,
    private val exceptionReporter: ExceptionReporter,
) {

    operator fun invoke(
        originalExpression: String,
        expression: String,
        withNikudAndTeamim: Boolean,
        searchMode: SearchMode,
    ): ResultRegex {

        Napier.d("originalExpression: $originalExpression, expression: $expression")
        var regex = expression
        var string = ""

        var firstColonIsPartOfRegex = false

        val isDashInside = originalExpression.contains('-')

        val shouldAutoWrapRegexForHighlight =
            searchMode == SearchMode.REGULAR_SEARCH
                    && !isDashInside // example: 4-משיח
                    // אם אין capturing groups אז עוטפים את הביטוי כולו בסוגריים כדי שהוא יודגש בתוצאות החיפוש
                    && !hasBalancedParenthesesGroupHeuristic(originalExpression)

        if (shouldAutoWrapRegexForHighlight) {
            if (regex.take(2).contains("^")) {
                firstColonIsPartOfRegex = true
                regex = regex.replace("^", "${StringConstants.Regex.PASUK_BEGINNING}(") + ")"
            } else {
                regex = "(%s)".formatString(regex)
                //                    regex = "(?<="+pasukBeginning+")" + regex; //positive look-behind
            }
        } else if (regex.take(2).contains("^")) {
            firstColonIsPartOfRegex = true
            regex = regex
                .replace("(^", "^(")
                .replace("^", StringConstants.Regex.PASUK_BEGINNING)
        }

        // "_" for a word
        regex = if (withNikudAndTeamim) {
            //            regex = regex.replace("_", "(?:"+alef_tav + not_alef_tav_space+"*)+");
            regex.replace("_", "(?:${StringConstants.Regex.NOT_SPACE_MAKAF}{2,})") // at least two chars in a word
            // TODO: 22/06/2016  add "_L_" option to search a letter anywhere inside a word
        } else {
            regex.replace("_", "(?:${StringConstants.Regex.ALEF_TAV})+")
        }
        regex = regex.replaceRegex(regex = "[:$]", replacement = StringConstants.COLON_SPLITTER) // : or $ for end of verse
        //            regex = regex.replaceAll("[:\\$]", COLON_SPLITTER);  // : or $ for end of verse
        regex = regex.replaceRegex(regex = "\\?${StringConstants.COLON_SPLITTER}", replacement = "\\?:") // : or $ for end of verse
        //        regex = regex.replaceAll("\\s", "(?:[ "+makaf+"])");  // space equivalent also to makaf
        regex = regex.replaceRegex(
            regex = "\\s",  // space equivalent also to makaf and also to
            replacement = "(?:[ ${StringConstants.MAKAF}]|(?: ${StringConstants.PASEQ} ))"
        ) // (space pasek space)
        regex = regex.replace("!", StringConstants.Regex.WORD_BOUNDARY) // ! for word boundary

//        regex = regex.replace(".", "[^\\s"+COLON_SPLITTER+makaf+"]"); // . for non-(space,colon,makaf) char
//        regex = regex.replace("~", "(?:(?:[\\s"+pasek+makaf+"]|.))"); // ~ - tilde for a single letter or space/makaf/pasek
        regex = regex.replace(
            "~",
            "(?:[\\s${StringConstants.PASEQ}${StringConstants.MAKAF}]|.)"
        ) // ~ - tilde for a single letter or space/makaf/pasek
        regex = regex.replace(".", "(?:${StringConstants.Regex.ALEF_TAV}@)") // . - dot for a single letter
        Napier.d("regex before converting R: $regex")

//        String not_alef_tav_space_not_dagesh = StringUtils.chop(not_alef_tav_space) + dagesh + "]";
//        regex = regex.replace("@)R", ")" + not_alef_tav_space_not_dagesh + "*"); // R for rafe letter = no dagesh.
        regex = regex.replace("@)R", ")(?!${StringConstants.DAGESH})@") // R for rafe letter = no dagesh.
        Napier.d("regex before converting @: $regex")

        //this statement must come after replacing ^ char
        regex = regex.replace("@", "${StringConstants.Regex.NOT_ALEF_TAV_SPACE}*") // @ for non-letter char.

//        regex = regex.replace("@", "(?:"+not_alef_tav_space+"*)"); // @ for non-letter char.
        if (searchMode == SearchMode.FIRST_LAST_LETTERS_IN_NAME) {
            val firstLetter: Char
            val lastLetter: Char
            if (originalExpression.length > 1) {
                firstLetter = originalExpression.first()
                lastLetter = originalExpression.last()
                val alephTavRange = 'א'..'ת'
                if (firstLetter !in alephTavRange || lastLetter !in alephTavRange) {
                    // אות ראשונה או אות אחרונה לא בעברית
                    return ResultRegex(
                        string = stringResolver.resolveString(StringDesc.Resource(SharedRes.strings.illegal_name_to_be_searched)),
                        regexResult = "",
                        actualRegex = null,
                        firstColonIsPartOfRegex = firstColonIsPartOfRegex
                    )
                }
            } else {
                // שם קצר מדי
                return ResultRegex(
                    string = stringResolver.resolveString(StringDesc.Resource(SharedRes.strings.illegal_name_to_be_searched)),
                    regexResult = "",
                    actualRegex = null,
                    firstColonIsPartOfRegex = firstColonIsPartOfRegex
                )
            }
            regex = (StringConstants.COLON_SPLITTER + "\\s" + "(?:\\{.+?\\}\\s)?" + StringConstants.Regex.ALEF_TAV + "{1,3}" + "\\s"
                    + "(" + firstLetter + StringConstants.Regex.NOT_ALEF_TAV_SPACE + "*)"
                    + "[^" + StringConstants.COLON_SPLITTER + "]" + "*"
                    + "(" + lastLetter + StringConstants.Regex.NOT_ALEF_TAV_SPACE + "*)"
                    + StringConstants.COLON_SPLITTER)
            firstColonIsPartOfRegex = true
        }
        Napier.d("SearchUseCase_regex: $regex")

        val regexObj = runCatching { Regex(pattern = regex) }
            .onFailure { e ->
                exceptionReporter.report(e)
                string = stringResolver.resolveString(
                    StringDesc.Resource(SharedRes.strings.illegal_regular_expression)
                ) + "\n" + e.toString()
            }
            .getOrNull()

        return ResultRegex(
            string = string,
            regexResult = regex,
            actualRegex = regexObj,
            firstColonIsPartOfRegex = firstColonIsPartOfRegex
        )
    }

    /**
     * Heuristic function that checks whether a regex string contains at least one
     * (non-escaped) pair of balanced parentheses.
     *
     * This is a lightweight alternative to `Pattern.groupCount()` and does NOT
     * fully parse the regex. It simply:
     *
     * - Ignores escaped parentheses (e.g. `\(` or `\)`)
     * - Ensures parentheses are balanced (no premature closing)
     * - Verifies that at least one opening parenthesis `(` exists
     *
     * ⚠️ Limitations:
     * - Does NOT distinguish between capturing and non-capturing groups
     *   (e.g. `(abc)` vs `(?:abc)` vs `(?=abc)` are all treated the same)
     * - Does NOT handle complex regex syntax (character classes like `[()]`, etc.)
     * - Intended only as a simple heuristic, not a full regex parser
     *
     * ### Examples:
     *
     * ```
     * hasBalancedParenthesesGroupHeuristic("abc")            == false
     * hasBalancedParenthesesGroupHeuristic("(abc)")          == true
     * hasBalancedParenthesesGroupHeuristic("(a)(b)")         == true
     * hasBalancedParenthesesGroupHeuristic("!(.)(.)")        == true
     *
     * hasBalancedParenthesesGroupHeuristic("(abc")           == false   // missing ')'
     * hasBalancedParenthesesGroupHeuristic("abc)")           == false   // extra ')'
     *
     * hasBalancedParenthesesGroupHeuristic("\\(abc\\)")      == false   // escaped parentheses
     *
     * hasBalancedParenthesesGroupHeuristic("(?:abc)")        == true    // non-capturing (treated as group)
     * hasBalancedParenthesesGroupHeuristic("(?=abc)")        == true    // lookahead (treated as group)
     * ```
     *
     * @param regex The regex string to analyze
     * @return `true` if the string contains at least one balanced (non-escaped) parenthesis pair
     */
    private fun hasBalancedParenthesesGroupHeuristic(regex: String): Boolean {
        var balance = 0
        var hasOpenParenthesis = false
        var isEscaped = false

        for (char in regex) {
            when {
                isEscaped -> {
                    isEscaped = false
                }

                char == '\\' -> {
                    isEscaped = true
                }

                char == '(' -> {
                    hasOpenParenthesis = true
                    balance++
                }

                char == ')' -> {
                    balance--
                    if (balance < 0) return false
                }
            }
        }

        return hasOpenParenthesis && balance == 0
    }
}
